import { useState } from 'react';
import { Alert, Stack } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import GhostButton from '../../shared/button/GhostButton';
import { CaptionText, MinorHeading } from '../../shared/typography/PrimaryTypography';
import {
  fetchFileContentBlob,
  toProblemDetail
} from '../../../services/syntiro_honey/data_ingestion.service';
import { EsgRecord, ProblemDetail } from '../../../types/syntiro_honey';
import { formatProvenance } from './provenance';
import { isInlineViewable, readPageHint } from './source_file';

interface SourceFileLinkProps {
  /** The record whose original source file to open (needs file + provenance). */
  record: Pick<EsgRecord, 'fileId' | 'fileName' | 'rawPayload'>;
}

/** Revoke the object URL well after the new tab / download has consumed the bytes. */
const OBJECT_URL_TTL_MS = 60_000;

/**
 * The permanent, auditable link back to the file a record was extracted from
 * (Phase 5 traceability). Fetches the original bytes authenticated, then hands
 * them to the browser: a PDF/image opens inline in a new tab — jumping to the
 * cited page where the payload carries one — while other formats (xlsx/pptx)
 * download under their original filename. Works for every input type, so it is
 * the navigate-to-source baseline even where the in-context Worksheet preview
 * (Excel only) does not apply.
 * @returns {JSX.Element}
 */
export default function SourceFileLink({ record }: SourceFileLinkProps) {
  const [busy, setBusy] = useState<boolean>(false);
  const [problem, setProblem] = useState<ProblemDetail | null>(null);

  const locator = formatProvenance(record.rawPayload);
  const pageHint = readPageHint(record.rawPayload);

  const handleOpen = async (): Promise<void> => {
    setBusy(true);
    setProblem(null);
    try {
      const blob = await fetchFileContentBlob(record.fileId);
      const url = URL.createObjectURL(blob);
      if (isInlineViewable(blob.type)) {
        // Deep-link to the cited page for PDFs whose viewer honours the fragment.
        const href =
          blob.type === 'application/pdf' && pageHint !== null ? `${url}#page=${pageHint}` : url;
        window.open(href, '_blank', 'noopener');
      } else {
        // Non-inline (spreadsheets/presentations): download under the real name.
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = record.fileName;
        anchor.click();
      }
      window.setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_TTL_MS);
    } catch (error: unknown) {
      setProblem(toProblemDetail(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={0.75}>
      <MinorHeading weight="Medium">Source</MinorHeading>
      <CaptionText>
        {record.fileName}
        {locator !== null ? ` · ${locator}` : ''}
      </CaptionText>
      {problem !== null && (
        <Alert severity="error" onClose={() => setProblem(null)}>
          {problem.detail ?? problem.title ?? 'Could not open the source file.'}
        </Alert>
      )}
      <GhostButton
        dark
        outlined
        startIcon={<OpenInNewIcon />}
        disabled={busy}
        onClick={() => void handleOpen()}
        sx={{ alignSelf: 'flex-start' }}
      >
        Open original file
      </GhostButton>
    </Stack>
  );
}
